import {BalloonEventModel} from 'ts-core';
import React from 'react';
import {FiMap} from 'react-icons/fi';
import {useNavigate} from 'react-router-dom';
import {useTranslations} from '../../contexts/Translations';
import {trackMixpanel} from '../../utils/mixpanel';

export interface OpenOnMapButtonProps {
    event: BalloonEventModel;
    trackingSource?: string;
    /**
     * - `'icon'` (default): 36px circular icon-only button.
     * - `'pill'`: rounded pill with icon + label, solid balloon background.
     * - `'cta'`: full-width outlined pill button. Mirrors the mobile footer
     *   close-CTA styling used by the map sidebar; use as a primary bottom
     *   action on detail pages.
     */
    variant?: 'icon' | 'pill' | 'cta';
    className?: string;
}

export function OpenOnMapButton({
                                    event,
                                    trackingSource,
                                    variant = 'icon',
                                    className,
                                }: OpenOnMapButtonProps) {
    const {translated} = useTranslations();
    const navigate = useNavigate();

    const onClick = () => {
        trackMixpanel('EventDetailPage', 'OpenOnMap:Click', {
            eventId: event.id,
            slug: event.slug,
            source: trackingSource,
        });
        const target = event.slug ?? event.id;
        navigate(`/?select=${encodeURIComponent(target)}`);
    };

    if (variant === 'cta') {
        return (
            <button
                onClick={onClick}
                className={
                    className ??
                    'w-full inline-flex items-center justify-center gap-2 bg-balloon text-white hover:opacity-90 rounded-full px-4 py-3 font-medium transition-opacity'
                }
                aria-label={translated('explore_nearby_on_map')}
                title={translated('explore_nearby_on_map')}
            >
                <FiMap size={18}/>
                <span>{translated('explore_nearby_on_map')}</span>
            </button>
        );
    }

    if (variant === 'pill') {
        return (
            <button
                onClick={onClick}
                className={
                    className ??
                    'inline-flex items-center gap-2 rounded-full bg-balloon text-white px-4 py-2 text-sm font-medium hover:opacity-90 transition'
                }
                aria-label={translated('explore_nearby_on_map')}
                title={translated('explore_nearby_on_map')}
            >
                <FiMap size={18}/>
                <span>{translated('explore_nearby_on_map')}</span>
            </button>
        );
    }

    return (
        <button
            onClick={onClick}
            className={
                className ??
                'flex justify-center items-center w-[36px] h-[36px] text-balloon hover:text-white bg-transparent hover:bg-balloon rounded-full p-2'
            }
            aria-label={translated('explore_nearby_on_map')}
            title={translated('explore_nearby_on_map')}
        >
            <FiMap size={20}/>
        </button>
    );
}
